// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
'use client';
// Sélecteur de modules de la suite BIO24 — présent dans la ConnectionBar de
// chaque app, au même emplacement. Permet de passer de Site ↔ Manuel ↔ Qualité
// sans rupture (SSO cookie + chrome identique). Le module courant est mis en
// avant ; les autres sont des liens (affichés selon l'accès de l'utilisateur).
import React from 'react';
import { Icon } from './Icon';

export type ModuleKey = 'site' | 'manuel' | 'ged';
export interface ModuleAccess { site: boolean; manuel: boolean; ged: boolean }

const META: Record<ModuleKey, { label: string; icon: string }> = {
  site: { label: 'Site', icon: 'globe' },
  manuel: { label: 'Catalogue', icon: 'file' },
  ged: { label: 'Nafis', icon: 'shield' },
};

/** Modules souscrits par le laboratoire (forfait SaaS, field-config.org.modules).
 *  Un champ absent vaut « inclus » : un labo déjà installé ne perd pas ses
 *  modules parce que le forfait n'a pas encore été renseigné. */
export type TenantModules = Partial<Record<ModuleKey, boolean>>;

/** Accès PAR MODULE de la personne connectée — le champ `apps` du compte (H4),
 *  servi tel quel par `/api/auth/me` des trois modules. C'est la seule source
 *  qui dise ce que CETTE personne peut réellement ouvrir. */
export type UserApps = Partial<Record<ModuleKey, boolean>>;

/** Valeur d'`apps` d'un compte qui n'en porte pas encore : mêmes règles que le
 *  backfill serveur (`migrateUserApps`, backend/src/index.ts). N'est utilisé
 *  QUE dans ce cas ; dès que le compte porte `apps`, c'est `apps` qui décide. */
export function appsParDefaut(role: string | null | undefined): ModuleAccess {
  const staff = role === 'technician' || role === 'manager' || role === 'superadmin';
  if (staff) return { site: true, manuel: true, ged: true };
  if (role === 'partner') return { site: true, manuel: true, ged: false };
  return { site: false, manuel: false, ged: false };
}

/** Accès effectif = forfait du LABORATOIRE ∩ accès par module de la PERSONNE.
 *
 *  Les deux niveaux ne disent pas la même chose et ne se remplacent pas :
 *   · le forfait = ce que le client a acheté. Il plafonne tout le monde, super
 *     admin compris — un module hors forfait n'existe pas pour ce tenant ;
 *   · `apps` = ce que CETTE personne peut ouvrir, réglé compte par compte dans
 *     le module Site (Paramétrage → Utilisateurs).
 *
 *  ── MAN-8 : pourquoi le rôle ne suffisait pas ────────────────────────────
 *  Jusqu'ici l'accès se DÉDUISAIT du rôle (staff = les trois modules, confrère
 *  = site + manuel). Or `apps` existe depuis H4 et peut dire l'inverse : le
 *  sélecteur proposait « Qualité » à un technicien à qui on venait de le
 *  retirer, et cachait « Site » à quelqu'un qui y avait droit. Le module
 *  Qualité, lui, honorait déjà le vrai droit — l'écart n'était que visuel,
 *  mais un menu qui ment sur les droits est une promesse cassée à chaque clic.
 *  Le rôle ne sert donc plus que de repli pour un compte sans `apps`.
 *
 *  ── Ce que voit un visiteur ──────────────────────────────────────────────
 *  `apps === undefined` avec un rôle public (ou pas de rôle) → tout est faux,
 *  et `ModuleSwitcher` ne rend RIEN (moins de deux modules). Un visiteur non
 *  connecté ne doit rien apprendre de ce à quoi il n'a pas accès.
 *
 *  Ceci ne fait que masquer l'inaccessible. Le contrôle réel est côté serveur
 *  (proxy GED + whoami du moteur) : un bouton grisé n'a jamais protégé une URL. */
export function moduleAccessFor(
  role: string | null | undefined,
  modules?: TenantModules,
  apps?: UserApps | null,
): ModuleAccess {
  const dflt = appsParDefaut(role);
  // Clé absente dans `apps` = repli sur le défaut du rôle, jamais « ouvert » :
  // un objet partiel ne doit pas accorder plus que le compte n'a.
  const parApp = (k: ModuleKey) => (apps && typeof apps[k] === 'boolean' ? apps[k]! : dflt[k]);
  const perso: ModuleAccess = { site: parApp('site'), manuel: parApp('manuel'), ged: parApp('ged') };
  if (!modules) return perso;
  return {
    site: perso.site && modules.site !== false,
    manuel: perso.manuel && modules.manuel !== false,
    ged: perso.ged && modules.ged !== false,
  };
}

export function ModuleSwitcher({ current, urls, access, gedUrl }: {
  current: ModuleKey;
  urls: { site: string; manuel: string; ged: string };
  access: ModuleAccess;
  gedUrl?: string; // masque « Qualité » si l'URL GED n'est pas configurée
}) {
  const order: ModuleKey[] = ['site', 'manuel', 'ged'];
  const shown = order.filter((k) => access[k] && (k !== 'ged' || (gedUrl ?? urls.ged)));
  if (shown.length < 2) return null; // rien à commuter

  return (
    <span style={{ display: 'inline-flex', flex: '0 0 auto', background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.16)', borderRadius: 'var(--radius-pill)', padding: 3, gap: 2 }}>
      {shown.map((k) => {
        const m = META[k];
        const on = k === current;
        const inner = (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: 'var(--radius-pill)', fontFamily: 'var(--font-sans)', fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', background: on ? '#fff' : 'transparent', color: on ? 'var(--navy-900)' : 'rgba(255,255,255,0.72)' }}>
            <Icon name={m.icon} size={14} color={on ? 'var(--color-primary)' : 'currentColor'} />{m.label}
          </span>
        );
        return on
          ? <span key={k} aria-current="page">{inner}</span>
          /* ⚠️ LE MODULE CHANGE DE DOMAINE, ET ON LE DIT (relevé du 27/08/2026,
             P0.9). « Site » et « Catalogue » mènent à `www.bio24.tn` et
             `catalog.bio24.tn` — d'autres origines — dans le MÊME onglet, alors
             qu'ils sont posés comme des onglets internes. On quittait donc
             Qualité sans prévenir, en perdant sa place et son écran ; le retour
             se fait par le bouton « Précédent », ce que rien n'annonce.
             Le libellé du survol le nomme désormais, et le nom accessible avec
             lui. On ne force PAS `target="_blank"` : la suite se veut continue,
             et ouvrir un onglet par module en accumulerait un par passage. */
          : <a key={k} href={urls[k]} className="cb-btn" style={{ textDecoration: 'none', display: 'inline-flex' }}
              title={`Aller au module ${m.label} — vous quittez ce module`}
              aria-label={`Aller au module ${m.label} — vous quittez ce module`}>{inner}</a>;
      })}
    </span>
  );
}
